/**
 * Reptilian Agent - Sound System
 * Web Audio API based (Toss WebView compatible)
 * Multi-track BGM with sequential playback
 */

type SfxType =
  | 'tap'
  | 'purchase'
  | 'prestige'
  | 'achievement'
  | 'goldenEvent'
  | 'comboUp'
  | 'error'
  | 'upgrade'
  | 'boost'
  | 'conquest';

let audioCtx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let bgmGain: GainNode | null = null;
let sfxGain: GainNode | null = null;

let bgmVolume = 50;
let sfxVolume = 70;
let bgmPlaying = false;
let bgmSource: AudioBufferSourceNode | null = null;

const SAVE_KEY = 'reptilian-agent-sound';

// === BGM Track list ===
const BGM_TRACKS = [
  './audio/bgm_01.mp3', // Sneaky Spy
  './audio/bgm_02.mp3', // Funky Villain
  './audio/bgm_03.mp3', // Retro 8-bit
  './audio/bgm_04.mp3', // Jazz Heist
  './audio/bgm_05.mp3', // Disco Reptilian
  './audio/bgm_06.mp3', // Techno Takeover
  './audio/bgm_07.mp3', // Latin Mischief
  './audio/bgm_08.mp3', // Villain's Waltz
];
let currentTrackIndex = 0;
const trackBuffers: Map<number, AudioBuffer> = new Map();

// === Init ===

function initAudio(): boolean {
  if (audioCtx) return true;
  try {
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    audioCtx = new AC();
    masterGain = audioCtx.createGain();
    masterGain.gain.value = 1.0;
    masterGain.connect(audioCtx.destination);

    bgmGain = audioCtx.createGain();
    bgmGain.gain.value = bgmVolume / 100;
    bgmGain.connect(masterGain);

    sfxGain = audioCtx.createGain();
    sfxGain.gain.value = sfxVolume / 100;
    sfxGain.connect(masterGain);

    return true;
  } catch {
    return false;
  }
}

function resumeCtx() {
  if (audioCtx && audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
}

// === Settings ===

function loadSoundSettings() {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (raw) {
      const s = JSON.parse(raw);
      bgmVolume = s.bgmVol ?? 50;
      sfxVolume = s.sfxVol ?? 70;
      if (typeof s.trackIdx === 'number') currentTrackIndex = s.trackIdx;
    }
  } catch { /* ignore */ }
}

function saveSoundSettings() {
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify({
      bgmVol: bgmVolume, sfxVol: sfxVolume, trackIdx: currentTrackIndex,
    }));
  } catch { /* ignore */ }
}

// === SFX (oscillator-based) ===

function playNote(
  freq: number, duration: number, type: OscillatorType = 'sine',
  vol = 0.15, startDelay = 0
) {
  if (!audioCtx || !sfxGain || sfxVolume <= 0) return;
  resumeCtx();

  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();
  osc.connect(gain);
  gain.connect(sfxGain);

  osc.type = type;
  const t = audioCtx.currentTime + startDelay;
  osc.frequency.setValueAtTime(freq, t);
  gain.gain.setValueAtTime(vol, t);
  gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

  osc.start(t);
  osc.stop(t + duration);
}

export function playSfx(type: SfxType) {
  if (sfxVolume <= 0) return;
  if (!initAudio()) return;

  switch (type) {
    case 'tap':
      playNote(400, 0.04, 'square', 0.06);
      playNote(800, 0.06, 'sine', 0.1);
      playNote(150, 0.05, 'triangle', 0.12);
      break;

    case 'purchase':
      playNote(1200, 0.08, 'sine', 0.12);
      playNote(1600, 0.12, 'sine', 0.1, 0.06);
      break;

    case 'prestige':
      playNote(392, 0.15, 'sine', 0.15, 0);
      playNote(523, 0.15, 'sine', 0.15, 0.12);
      playNote(659, 0.15, 'sine', 0.15, 0.24);
      playNote(784, 0.2, 'sine', 0.18, 0.36);
      playNote(1047, 0.3, 'sine', 0.12, 0.48);
      break;

    case 'achievement':
      playNote(523, 0.15, 'sine', 0.15, 0);
      playNote(659, 0.15, 'sine', 0.15, 0.08);
      playNote(784, 0.2, 'sine', 0.18, 0.16);
      playNote(1568, 0.1, 'sine', 0.06, 0.28);
      break;

    case 'goldenEvent':
      playNote(880, 0.1, 'triangle', 0.12);
      playNote(1320, 0.15, 'triangle', 0.12, 0.05);
      playNote(1760, 0.12, 'triangle', 0.1, 0.12);
      break;

    case 'comboUp':
      playNote(660, 0.06, 'triangle', 0.08);
      playNote(880, 0.08, 'triangle', 0.06, 0.04);
      break;

    case 'error':
      playNote(200, 0.2, 'square', 0.06);
      break;

    case 'upgrade':
      playNote(440, 0.08, 'sine', 0.1);
      playNote(660, 0.1, 'sine', 0.08, 0.06);
      break;

    case 'boost':
      playNote(523, 0.1, 'sine', 0.12, 0);
      playNote(784, 0.12, 'sine', 0.1, 0.06);
      playNote(1047, 0.15, 'sine', 0.12, 0.12);
      break;

    case 'conquest':
      playNote(392, 0.15, 'sine', 0.2, 0);
      playNote(523, 0.15, 'sine', 0.2, 0.1);
      playNote(659, 0.15, 'sine', 0.2, 0.2);
      playNote(784, 0.2, 'sine', 0.25, 0.3);
      playNote(1047, 0.25, 'sine', 0.2, 0.45);
      playNote(1568, 0.3, 'triangle', 0.15, 0.6);
      break;
  }
}

// === BGM (multi-track sequential playback) ===

async function loadTrack(index: number): Promise<AudioBuffer | null> {
  if (trackBuffers.has(index)) return trackBuffers.get(index)!;
  if (!audioCtx) return null;
  try {
    const res = await fetch(BGM_TRACKS[index]);
    if (!res.ok) return null;
    const arrayBuf = await res.arrayBuffer();
    const buf = await audioCtx.decodeAudioData(arrayBuf);
    trackBuffers.set(index, buf);
    return buf;
  } catch (e) {
    console.warn(`[BGM] track ${index} load failed:`, e);
    return null;
  }
}

function playTrack(buf: AudioBuffer) {
  if (!audioCtx || !bgmGain) return;

  bgmSource = audioCtx.createBufferSource();
  bgmSource.buffer = buf;
  bgmSource.loop = false; // don't loop single track — advance to next
  bgmSource.connect(bgmGain);
  bgmSource.start(0);
  bgmPlaying = true;

  bgmSource.onended = () => {
    if (!bgmPlaying) return; // was stopped manually
    // Advance to next track
    bgmPlaying = false; // reset so startBgm can proceed
    bgmSource = null;
    currentTrackIndex = (currentTrackIndex + 1) % BGM_TRACKS.length;
    saveSoundSettings();
    startBgm();
  };
}

async function startBgm() {
  if (bgmPlaying) return;
  if (!initAudio()) return;
  if (!audioCtx || !bgmGain) return;
  resumeCtx();

  const buf = await loadTrack(currentTrackIndex);
  if (!buf || bgmPlaying) return;

  playTrack(buf);

  // Preload next track in background
  const nextIdx = (currentTrackIndex + 1) % BGM_TRACKS.length;
  loadTrack(nextIdx);
}

function stopBgm() {
  if (bgmSource) {
    bgmSource.onended = null; // prevent auto-advance
    try { bgmSource.stop(); } catch { /* ignore */ }
    bgmSource = null;
  }
  bgmPlaying = false;
}

// === Public API ===

export function initSound() {
  loadSoundSettings();
  initAudio();

  const startOnInteraction = () => {
    if (!initAudio()) return;
    resumeCtx();
    if (bgmVolume > 0 && !bgmPlaying) {
      startBgm();
    }
  };

  ['touchstart', 'click', 'pointerdown'].forEach(evt => {
    document.addEventListener(evt, startOnInteraction, { once: true, passive: true });
  });

  // Background handling
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      if (bgmPlaying) stopBgm();
    } else {
      if (bgmVolume > 0) startBgm();
    }
  });
}

export function getBgmVolume(): number { return bgmVolume; }
export function getSfxVolume(): number { return sfxVolume; }

export function setBgmVolume(vol: number) {
  bgmVolume = Math.max(0, Math.min(100, Math.round(vol)));
  if (bgmGain) bgmGain.gain.value = bgmVolume / 100;
  if (bgmVolume > 0 && !bgmPlaying) startBgm();
  else if (bgmVolume <= 0 && bgmPlaying) stopBgm();
  saveSoundSettings();
}

export function setSfxVolume(vol: number) {
  sfxVolume = Math.max(0, Math.min(100, Math.round(vol)));
  if (sfxGain) sfxGain.gain.value = sfxVolume / 100;
  saveSoundSettings();
}

export function isBgmEnabled(): boolean { return bgmVolume > 0; }
export function isSfxEnabled(): boolean { return sfxVolume > 0; }

/** Get current track info */
export function getCurrentTrackIndex(): number { return currentTrackIndex; }
export function getTotalTracks(): number { return BGM_TRACKS.length; }
